<template>
  <div class="flex flex-col w-full flex-1 gap-6 h-full overflow-y-auto pb-10">
    <!-- Header -->
    <div class="flex flex-col w-full gap-3">
      <div class="flex items-center justify-between w-full py-2">
        <h1 class="text-lg font-semibold text-[#0b1220]">My Tasks</h1>
        <div class="flex items-center gap-2">
          <!-- Assignee Filter Dropdown -->
          <template v-if="can('filter-assignee-tasks')">
            <v-select
              :options="assigneeOptions"
              v-model="assigneeFilter"
              label="name"
              track-by="id"
              :filterable="false"
              @search="onAssigneeSearch"
              placeholder="Filter by assignee"
              class="w-[200px] bg-white"
              clearable
            >
              <template #option="{ name, email, roles }">
                <span class="max-w-[200px] whitespace-break-spaces break-all">
                  {{ name }}<br />
                  <small
                    >{{ roles && roles[0]?.name ? roles[0].name + ' | ' : '' }}{{ email }}</small
                  >
                </span>
              </template>
              <template #selected-option="{ name }">
                <span>
                  {{ name }}
                </span>
              </template>
            </v-select>
          </template>

          <input
            v-model="taskSearchQuery"
            type="text"
            placeholder="Type to search"
            class="border rounded-md px-3 py-2 w-72 focus:outline-none"
          />

          <button
            :disabled="createDisabled"
            @click="toggleCreateCard"
            class="px-3 py-2 bg-emerald-500 text-white rounded-md shadow hover:brightness-95"
            v-if="can('create-task')"
          >
            + Create Task
          </button>
        </div>
      </div>

      <!-- Filters (under search) aligned right like screenshot -->
      <div class="flex justify-between items-center w-full">
        <div>
          <!-- Tabs for Pending and Completed -->
          <div class="flex gap-2 mb-2">
            <button
              :class="[
                'px-4 py-2 rounded-md border',
                selectedTab === 'all'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-emerald-500 border-emerald-500',
              ]"
              @click="selectedTab = 'all'"
            >
              All
            </button>
            <button
              :class="[
                'px-4 py-2 rounded-md border',
                selectedTab === 'pending'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-emerald-500 border-emerald-500',
              ]"
              @click="selectedTab = 'pending'"
            >
              Pending
            </button>
            <button
              :class="[
                'px-4 py-2 rounded-md border',
                selectedTab === 'completed'
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-white text-emerald-500 border-emerald-500',
              ]"
              @click="selectedTab = 'completed'"
            >
              Completed
            </button>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-for="option in severityOptions"
            :key="option.value"
            @click="applySeverityFilter(option.value)"
            :class="[
              'text-sm px-3 py-1 rounded-full border flex items-center gap-2',
              selectedSeverity === option.value ? 'ring-2 ring-offset-1' : '',
            ]"
            :style="{
              borderColor: option.border,
              backgroundColor: selectedSeverity === option.value ? option.bg : 'white',
              color: selectedSeverity === option.value ? option.fg : option.fg,
            }"
          >
            <span v-if="option.value === 'urgent'">
              <ExclamationTriangleIcon class="w-4 h-4" />
            </span>
            <span v-if="option.value === 'medium'">
              <ClockIcon class="w-4 h-4" />
            </span>
            <span v-if="option.value === 'low'">
              <FlagIcon class="w-4 h-4" />
            </span>
            {{ option.label }}
            <span v-if="option.count !== undefined && option.value !== 'all'" class="ml-1 text-xs"
              >({{ option.count }})</span
            >
          </button>
        </div>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="tasksLoading" class="py-8 flex justify-center items-center">
      <svg
        class="animate-spin h-6 w-6 text-gray-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          class="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
        ></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
      <span class="ml-3 text-gray-600">Loading tasks...</span>
    </div>

    <!-- Grid: max 4 columns on xl -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <!-- Create card (shown only when toggled) -->
      <div v-if="showCreateCard" class="border rounded-lg p-4 bg-white shadow">
        <div class="flex items-center justify-between mb-3">
          <h2 class="font-semibold">Create Task</h2>
          <button @click="toggleCreateCard" class="text-gray-500 hover:text-gray-800">
            <XMarkIcon class="w-5 h-5" />
          </button>
        </div>

        <div class="flex flex-col gap-3">
          <!-- Task Type (Select) -->
          <label class="text-sm font-medium">Task Type</label>
          <Select
            v-model="newTask.task_type_id"
            :options="taskTypeOptions"
            label="label"
            :reduce="(o) => o.value"
            placeholder="Select"
            :clearable="false"
            disabled
          />

          <label class="text-sm font-medium">Title</label>
          <input
            v-model="newTask.task_title"
            type="text"
            placeholder="Task title"
            class="border rounded px-3 py-2"
          />
          <template v-if="can('filter-assignee-tasks')">
            <label class="text-sm font-medium">Assignee</label>
            <v-select
              :options="assigneeOptions"
              v-model="newTask.assignee"
              label="name"
              track-by="id"
              :filterable="false"
              @search="onAssigneeSearch"
              placeholder="Search by name or email"
              class="w-full"
            >
              <template #option="{ name, email, roles }">
                <span>
                  {{ name }}<br />
                  <small>{{ roles[0]?.name }} | {{ email }}</small>
                </span>
              </template>
              <template #selected-option="{ name, email }">
                <span> {{ name }} {{ email ? `| ${email}` : '' }} </span>
              </template>
            </v-select>
          </template>

          <label class="text-sm font-medium">Severity</label>
          <Select
            v-model="newTask.severity"
            :options="severityOptionsForSelect"
            label="label"
            :reduce="(o) => o.value"
            placeholder="Select severity"
          />

          <label class="text-sm font-medium">Select Order</label>
          <v-select
            :options="orderOptions"
            v-model="newTask.relatable_order"
            label="order_no"
            track-by="id"
            :filterable="false"
            @search="onOrderSearch"
            placeholder="Search by Order No."
            class="w-full"
          >
            <template #option="{ order_no, customer_reference_no }">
              <span>
                Order# {{ order_no }}
                <br />
                <small>Ref: {{ customer_reference_no }}</small>
              </span>
            </template>
            <template #selected-option="{ order_no, customer_reference_no }">
              <span>
                Order# {{ order_no }}
                {{ customer_reference_no ? `|Ref: ${customer_reference_no}` : '' }}</span
              >
            </template>
          </v-select>
          <div class="flex gap-2">
            <button
              @click="submitCreateTask"
              :disabled="creating"
              class="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-md"
            >
              <template v-if="creating">
                <svg class="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                Creating...
              </template>
              <template v-else>Create</template>
            </button>
            <button @click="resetCreateForm" class="px-4 py-2 border rounded-md">Reset</button>
          </div>
        </div>
      </div>

      <!-- If no tasks -->
      <div
        v-if="tasks.length === 0 && !showCreateCard"
        class="col-span-full text-center py-12 border rounded-lg"
      >
        <p class="text-gray-600 mb-3">No tasks found. Create one to get started.</p>
        <button
          @click="showCreateCard = true"
          class="px-4 py-2 bg-emerald-500 text-white rounded-md"
          v-if="can('create-task')"
        >
          Create Task
        </button>
      </div>

      <!-- Task cards -->
      <div
        v-for="(task, taskIndex) in filteredTasks"
        :key="task.id"
        :class="['rounded-lg p-4 shadow border', severityCardClass(task.severity)]"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3">
            <div :class="['rounded-full bg-white/40 ', severityIconClass(task.severity)]">
              <template v-if="task.task_type_id === 1">
                <MapPinIcon class="w-6 h-6" />
              </template>
              <template v-else-if="task.task_type_id === 2">
                <PhotoIcon class="w-6 h-6" />
              </template>
              <template v-else>
                <ClipboardDocumentListIcon class="w-6 h-6" />
              </template>
            </div>

            <div>
              <h3 class="font-semibold text-sm mb-2">{{ task.task_title }}</h3>
              <p class="text-xs text-gray-500">Ref: {{ task.order?.customer_reference_no }}</p>
            </div>
          </div>
          <div>
            <span
              :class="[
                'text-sm border rounded-full px-2 py-1 text-white',
                severityOrderNoClass(task.severity),
              ]"
            >
              # {{ task?.order?.order_no }}
            </span>
          </div>
        </div>

        <div class="mt-4 text-xs text-gray-700">
          <p v-if="task.task_type_id === 1">
            Customer has requested address verification before shipment
          </p>
          <div class="flex items-center justify-between w-full mt-2">
            <div class="text-xs text-gray-500 mt-1 flex items-center gap-2">
              <ClockIcon class="w-4 h-4" />
              <span>{{ formatRelative(task.created_at) }}</span>
            </div>
            <div
              :class="[
                'text-xs font-medium px-2 py-0.5 rounded-[6px] w-max',
                severityBadgeClass(task.severity),
              ]"
            >
              {{ task.severity }}
            </div>
          </div>
          <div class="mt-2 text-sm text-gray-500 font-bold">
            {{ task.user.name }}
          </div>
          <!-- Shipping Address Display -->
          <div
            v-if="task.order?.shipping_address && !addressEditIndex[taskIndex]"
            class="mt-4 border rounded-lg p-4 bg-white text-sm shadow-sm"
          >
            <span class="font-semibold text-gray-800 text-sm">Shipping Address:</span>
            <p class="text-gray-700 mt-2">
              {{ task.order?.shipping_address?.first_name }}
              {{ task.order?.shipping_address?.last_name }}
            </p>
            <p class="text-gray-600">{{ task.order?.shipping_address?.company_name }}</p>

            <p class="text-gray-700">
              {{ task.order?.shipping_address?.address1 }}
              <span v-if="task.order?.shipping_address?.address2">
                , {{ task.order?.shipping_address?.address2 }}
              </span>
            </p>
            <p class="text-gray-700">
              {{ task.order?.shipping_address?.city }},
              {{ task.order?.shipping_address?.state }}
              {{ task.order?.shipping_address?.zip_code }}
            </p>
            <p class="text-gray-700">{{ task.order?.shipping_address?.country }}</p>

            <div class="mt-2 space-y-1">
              <p class="text-gray-600">{{ task.order?.shipping_address?.phone_number }}</p>
              <p class="text-gray-600">{{ task.order?.shipping_address?.email }}</p>
            </div>
          </div>

          <!-- Shipping Address Edit Form -->
          <div v-if="addressEditIndex[taskIndex]" class="mt-3 text-base flex flex-col gap-2">
            <input
              v-model="task.editForm.first_name"
              placeholder="First Name"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.last_name"
              placeholder="Last Name"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.company_name"
              placeholder="Company"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.address1"
              placeholder="Address Line 1"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.address2"
              placeholder="Address Line 2"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.city"
              placeholder="City"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.state"
              placeholder="State"
              class="border rounded px-3 py-2"
            />
            <input
              v-model="task.editForm.zip_code"
              placeholder="Zip Code"
              class="border rounded px-3 py-2"
            />
            <Select
              v-model="task.editForm.country"
              :options="countries"
              track-by="id"
              label="name"
              placeholder="Select Country"
              class="bg-white"
              :reduce="(country) => country.name"
              :key="`${countries.toString()}_${addressEditIndex[taskIndex]}`"
            />

            <input
              v-model="task.editForm.phone_number"
              placeholder="Phone Number"
              class="border rounded px-2 py-1"
            />
            <input
              v-model="task.editForm.email"
              placeholder="Email"
              class="border rounded px-2 py-1"
            />

            <div class="flex gap-2 mt-2 w-full justify-end">
              <button
                @click="saveAddress(task, taskIndex)"
                class="px-3 py-1 bg-emerald-500 text-white rounded"
              >
                Save
              </button>
              <button @click="cancelEditAddress(taskIndex)" class="px-3 py-1 border rounded">
                Cancel
              </button>
            </div>
          </div>
          <div class="flex gap-2 items-center w-full mt-4">
            <button
              v-if="task.status !== 'completed'"
              class="px-3 py-1 w-full bg-transparent text-black font-[500] rounded flex items-center gap-2 justify-center border border-emerald-700 text-base"
              @click="startEditAddress(task, taskIndex)"
            >
              <PencilSquareIcon class="size-5" /> Edit
            </button>
            <button
              @click="markComplete(task)"
              v-if="task.status !== 'completed'"
              class="px-3 py-1 bg-primary w-full text-white font-[500] rounded flex items-center gap-2 justify-center border border-primary text-base"
            >
              <CheckCircleIcon class="size-5" /> Complete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Select from '@/components/Shared/Select.vue';
import { can } from '@/composables';
import { showAlert } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  MapPinIcon,
  PencilSquareIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import debounce from 'lodash/debounce';
import { computed, ComputedRef, onMounted, ref, watch } from 'vue';

// --- State & UI ---
const taskSearchQuery = ref(localStorage.getItem("taskSearchQuery") || "");
const selectedSeverity = ref(localStorage.getItem("selectedSeverity") || "all");
const selectedTab = ref(localStorage.getItem("selectedTab") || "all");
const assigneeFilter = ref(
  localStorage.getItem("assigneeFilter")
    ? JSON.parse(localStorage.getItem("assigneeFilter")!)
    : null
);
const tasksLoading = ref(false);
const creating = ref(false);
const showCreateCard = ref(false);
const addressEditIndex = ref<Array<boolean>>([]);
// tasks from API
const tasks = ref<Array<any>>([]);
const orderOptions = ref<Array<any>>([]);
const countries = ref<any[]>([]);
// Assignee filter state
async function fetchCountries() {
  try {
    const res = await http.get('/addresses/countries');
    countries.value = res.data.result ?? [];
  } catch (err) {
    handleResponseException(err);
  }
}

function startEditAddress(task: any, index: number) {
  addressEditIndex.value[index] = true;
  task.editForm = { ...task.order.shipping_address };
}

function cancelEditAddress(index) {
  addressEditIndex.value[index] = false;
}

async function saveAddress(task: any, index) {
  const hasChanges = JSON.stringify(task.editForm) !== JSON.stringify(task.shipping_address);
  const payload: any = { status: task.status };
  if (hasChanges) {
    payload.shipping_address = { ...task.editForm };
  }

  try {
    await http.post(`/user-tasks/${task.id}/status`, payload);
    if (hasChanges) {
      task.order.shipping_address = { ...task.editForm };
    }
    addressEditIndex.value[index] = false;
  } catch (err) {
    handleResponseException(err);
  }
}

onMounted(() => {
  fetchCountries();
});

// dropdown options (replace with real options from API if needed)
const taskTypeOptions = [
  { label: 'Confirm Shipping Address', value: 1 },
  { label: 'Upload Sample Pictures', value: 2 },
  { label: 'Other', value: 3 },
];

// severity options with colors matching the screenshot
const severityOptions = [
  { label: 'All', value: 'all', bg: 'white', fg: '#0b1220', border: '#E6EEF7', count: 0 },
  { label: 'Normal', value: 'normal', bg: '#f0fff4', fg: '#065f46', border: '#dafcd6', count: 0 },
  { label: 'Low', value: 'low', bg: '#f0fff4', fg: '#065f46', border: '#dafcd6', count: 0 },
  { label: 'Medium', value: 'medium', bg: '#f0f8ff', fg: '#0369a1', border: '#dbeafe', count: 0 },
  { label: 'High', value: 'high', bg: '#fff1f0', fg: '#cf222e', border: '#ffd6d6', count: 0 },
  {
    label: 'Critical',
    value: 'critical',
    bg: '#f0fff4',
    fg: '#065f46',
    border: '#dafcd6',
    count: 0,
  },
];
const severityOptionsForSelect = severityOptions.filter((s) => s.value !== 'all');

// create form model
const newTask = ref({
  task_type_id: 1,
  task_title: '',
  assignee: null as Record<string, any> | null,
  severity: '' as 'normal' | 'low' | 'medium' | 'high' | 'critical',
  status: 'pending',
  relatable_order: null as Record<string, string> | null,
  relatable_type: 'order',
});

const assigneeOptions = ref<Array<any>>([]);
const allAssignees = ref<Array<any>>([]);
const onAssigneeSearch = debounce((query: string, loading) => {
  loading(true);
  if (!query || query.length < 2) {
    assigneeOptions.value = allAssignees.value;
    loading(false);
    return;
  }
  const q = query.toLowerCase();
  assigneeOptions.value = allAssignees.value.filter(
    (a) => a.name?.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q)
  );
  loading(false);
}, 300);

async function fetchAllAssignees() {
  try {
    const res = await http.get('user-tasks/assignees/list');
    allAssignees.value = res.data.result ?? res.data ?? [];
    assigneeOptions.value = allAssignees.value;
  } catch (err) {
    handleResponseException(err);
    allAssignees.value = [];
    assigneeOptions.value = [];
  }
}

// --- Helpers & computed ---
const filteredTasks = computed(() => {
  let result = tasks.value;
  // Filter by tab (pending/completed)
  if (selectedTab.value === 'pending') {
    result = result.filter((t) => t.status === 'pending');
  } else if (selectedTab.value === 'completed') {
    result = result.filter((t) => t.status === 'completed');
  }
  // Filter by assignee
  if (assigneeFilter.value && assigneeFilter.value.id) {
    result = result.filter((t) => t.user && t.user.id === assigneeFilter.value.id);
  }
  if (selectedSeverity.value !== 'all') {
    result = result.filter((t) => t.severity === selectedSeverity.value);
  }
  if (taskSearchQuery.value.trim()) {
    const q = taskSearchQuery.value.toLowerCase();
    result = result.filter(
      (t) =>
        String(t.task_title).toLowerCase().includes(q) ||
        String(t.order?.order_no).includes(q) ||
        String(t.user?.name).toLowerCase().includes(q) ||
        String(t.user?.email).toLowerCase().includes(q)
    );
  }
  return result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
});
function severityBadgeClass(sev: string | undefined) {
  switch (sev) {
    case 'critical':
      return 'bg-red-100 text-red-600 border border-red-500';
    case 'high':
      return 'bg-orange-100 text-orange-600 border border-orange-500';
    case 'medium':
      return 'bg-blue-100 text-blue-600 border border-blue-500';
    case 'low':
      return 'bg-yellow-100 text-yellow-600 border border-yellow-500';
    case 'normal':
    default:
      return 'bg-green-100 text-green-600 border border-green-500';
  }
}

function severityCardClass(sev: string | undefined) {
  switch (sev) {
    case 'critical':
      return 'bg-[#fff5f5] border-red-200';
    case 'high':
      return 'bg-[#fff8f1] border-orange-200';
    case 'medium':
      return 'bg-[#f5fbff] border-blue-200';
    case 'low':
      return 'bg-[#fffcf5] border-yellow-200';
    case 'normal':
    default:
      return 'bg-[#f5fff9] border-green-200';
  }
}
function severityOrderNoClass(sev: string | undefined) {
  switch (sev) {
    case 'critical':
      return 'bg-red-600 border-red-600';
    case 'high':
      return 'bg-orange-600 border-orange-600';
    case 'medium':
      return 'bg-blue-600 border-blue-600';
    case 'low':
      return 'bg-yellow-600 border-yellow-600';
    case 'normal':
    default:
      return 'bg-green-600 border-green-600';
  }
}
function severityIconClass(sev: string | undefined) {
  switch (sev) {
    case 'critical':
      return 'text-red-600';
    case 'high':
      return 'text-orange-600';
    case 'medium':
      return 'text-blue-600';
    case 'low':
      return 'text-yellow-600';
    case 'normal':
      return 'text-green-600';
    default:
      return 'text-green-600';
  }
}

function formatRelative(dateStr: string | undefined) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

// --- API calls ---
async function fetchTasks() {
  tasksLoading.value = true;
  try {
    const res = await http.get('user-tasks');
    // Expect API returns array in res.data
    if (res && res.data) {
      tasks.value = res.data.map((task: any) => ({
        ...task,
        editing: false,
      }));
      // optionally compute counts in severityOptions
      updateSeverityCounts();
    } else {
      tasks.value = [];
    }
  } catch (err) {
    handleResponseException(err);
    tasks.value = [];
  } finally {
    tasksLoading.value = false;
  }
}

function updateSeverityCounts() {
  const counts: Record<'normal' | 'low' | 'medium' | 'high' | 'critical', number> = {
    normal: 0,
    low: 0,
    medium: 0,
    high: 0,
    critical: 0,
  };

  tasks.value.forEach((t: any) => {
    if (t.severity && counts[t.severity as keyof typeof counts] !== undefined) {
      counts[t.severity as keyof typeof counts]++;
    }
  });

  severityOptions.forEach((o) => {
    if (o.value in counts) {
      o.count = counts[o.value as keyof typeof counts];
    }
  });
}

async function submitCreateTask() {
  if (
    !newTask.value.task_type_id ||
    !newTask.value.task_title ||
    !newTask.value.severity ||
    !newTask.value.relatable_order ||
    !newTask.value.assignee
  ) {
    showAlert('Oops', 'Please fill required fields', 'info');
    return;
  }
  creating.value = true;
  try {
    const payload = {
      task_type_id: newTask.value.task_type_id,
      task_title: newTask.value.task_title,
      user_id: newTask.value.assignee.id,
      severity: newTask.value.severity,
      status: newTask.value.status,
      relatable_id: newTask.value.relatable_order.id,
      relatable_type: newTask.value.relatable_type,
    };
    const res = await http.post('user-tasks', payload);
    await fetchTasks();
    resetCreateForm();
    showCreateCard.value = false;
  } catch (err) {
    handleResponseException(err);
    showAlert('Oops', 'Failed to create task', 'info');
    4;
  } finally {
    creating.value = false;
  }
}

function resetCreateForm() {
  newTask.value = {
    task_type_id: 1,
    task_title: '',
    assignee: null,
    severity: 'normal',
    status: 'pending',
    relatable_order: null,
    relatable_type: 'order',
  };
}

function toggleCreateCard() {
  showCreateCard.value = !showCreateCard.value;
  addressEditIndex.value = [];
}
const createDisabled: ComputedRef<boolean> = computed(() => {
  return creating.value;
});

// async function openTask(task: any) {
//   // fetch single task if you want to open detail view
//   try {
//     const res = await http.get(`user-tasks/${task.id}`);
//     const full = res.data.data ?? res.data;
//     // open modal or navigate to order — for now simply log
//   } catch (err) {
//     handleResponseException(err);
//   }
// }

async function markComplete(task: any) {
  try {
    await http.post(`user-tasks/${task.id}/status`, { status: 'completed' });
    await fetchTasks();
  } catch (err) {
    handleResponseException(err);
  }
}

// async function removeTask(task: any) {
//   if (!confirm('Delete this task?')) return;
//   try {
//     await http.delete(`user-tasks/${task.id}`);
//     await fetchTasks();
//   } catch (err) {
//     handleResponseException(err);
//     alert('Failed to delete');
//   }
// }

function applySeverityFilter(value: string) {
  selectedSeverity.value = value;
}

const onOrderSearch = debounce(async (query: string, loading) => {
  loading(true);
  if (!query || query.length < 2) {
    orderOptions.value = [];
    loading(false);
    return;
  }
  try {
    const res = await http.post(`/search-orders`, {
      query: query,
    });
    orderOptions.value = res.data.result ?? [];
  } catch (err) {
    handleResponseException(err);
    orderOptions.value = [];
  } finally {
    loading(false);
  }
}, 300);

// persist on change
watch(taskSearchQuery, (val) =>
  localStorage.setItem("taskSearchQuery", val)
);
watch(selectedSeverity, (val) =>
  localStorage.setItem("selectedSeverity", val)
);
watch(selectedTab, (val) => localStorage.setItem("selectedTab", val));
watch(assigneeFilter, (val) =>
  localStorage.setItem("assigneeFilter", JSON.stringify(val))
);
// --- lifecycle ---
onMounted(() => {
  fetchTasks();
  if (can('filter-assignee-tasks')) {
    fetchAllAssignees();
  }
});
</script>

